import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { ArrowRight, Activity, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import type { PositionRow } from '../../types';
import { POSITION_STATUS } from '../../constants/tradingConstants';
import { getBrandClasses } from '../../config/tenantConfig';
import PnlFlash from '../common/PnlFlash';
import EmptyStateWithRedirect from '../common/EmptyStateWithRedirect';
import { pickOpenPositionsEmpty } from '../../utils/emptyStates';

interface OpenPositionsCardProps {
  positions: PositionRow[];
  loading?: boolean;
}

const OpenPositionsCard: React.FC<OpenPositionsCardProps> = ({ positions, loading }) => {
  const brand = getBrandClasses();
  // EXIT_PENDING positions still hold market exposure — show alongside OPEN.
  const openPositions = positions.filter(
    p => p.status === POSITION_STATUS.OPEN || p.status === POSITION_STATUS.EXIT_PENDING
  );
  const display = openPositions.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Open Positions ({loading ? '\u2014' : openPositions.length})</CardTitle>
          {openPositions.length > 0 && (
            <Link to="/schedule?view=positions" className={`flex items-center gap-1 text-sm font-medium ${brand.link} ${brand.linkHover}`}>
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="px-6 py-8 text-center">
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto" />
            </div>
          </div>
        ) : display.length === 0 ? (
          <EmptyStateWithRedirect
            size="card"
            icon={<Activity className="w-8 h-8" />}
            {...pickOpenPositionsEmpty()}
          />
        ) : (
          <>
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Symbol</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Side</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Qty</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Entry</th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">P&L</th>
                  <th className="px-3 py-2.5 w-6" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {display.map(p => (
                  <tr key={p.positionId} className="group hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{p.symbol}</td>
                    <td className={cn('px-3 py-2.5 text-sm font-medium', p.side === 'BUY' ? brand.link : 'text-red-500 dark:text-red-400')}>
                      {p.side}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-900 dark:text-white tabular-nums">{p.qty}</td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-900 dark:text-white tabular-nums">
                      {p.entryPrice != null
                        ? p.entryPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '\u2014'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <PnlFlash value={p.pnl} className="text-sm font-medium" />
                    </td>
                    <td className="px-1 py-2.5">
                      <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {openPositions.length > 5 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
                <Link to="/schedule?view=positions" className={`text-sm font-medium ${brand.link} hover:underline`}>
                  +{openPositions.length - 5} more positions
                </Link>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OpenPositionsCard;
